import { useEffect, useState } from 'react';
import { Button, Card, Col, Dropdown, Form, Modal, Row, Table } from 'react-bootstrap';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import Authenticator from '../authenticator';
import Loading from '../components/loading';
import SuccessMessage from '../components/successMessage';
import ErrorMessage from '../components/errorMessage';
import Select from 'react-select';
import api from '../api/api';
import { Line } from 'react-chartjs-2';
import { useAppContext } from 'providers/AppProvider';
import { chartJsDefaultTooltip } from 'helpers/chartjs-utils';

export default function DosageEstudyForm() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isLoading, setLoading] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const [dosageStudyId, setDosageStudyId] = useState(searchParams.get('id'));

  const [nameToSave, setNameToSave] = useState('');
  const [codeToSave, setCodeToSave] = useState('');
  const [mortarPercentageToSave, setMortarPercentageToSave] = useState('');
  const [cementConsumptionPerCubicMeterToSave, setCementConsumptionPerCubicMeterToSave] = useState('');
  const [waterConsumptionPerCubicMeterToSave, setWaterConsumptionPerCubicMeterToSave] = useState('');
  const [incorporatedAirToSave, setIncorporatedAirToSave] = useState('');
  const [cementToSave, setCementToSave] = useState('');
  const [waterToSave, setWaterToSave] = useState('');
  const [waterReducingAdditiveToSave, setWaterReducingAdditiveToSave] = useState('');
  const [waterReducingAdditivePercentageToSave, setWaterReducingAdditivePercentageToSave] = useState('');

  // New state for "Outros Aditivos"
  const [otherAdditivesToSave, setOtherAdditivesToSave] = useState('');
  const [otherAdditivesPercentageToSave, setOtherAdditivesPercentageToSave] = useState('');
  const [otherAdditivesAndPercentagesToSave, setOtherAdditivesAndPercentagesToSave] = useState([]);

  const [concreteVolumeToSave, setConcreteVolumeToSave] = useState('');
  const [observationToSave, setObservationToSave] = useState('');

  const [maxDiameterToSave, setMaxDiameterToSave] = useState('');
  const [averageDiameterToSave, setAverageDiameterToSave] = useState('');
  const [finenessModulusToSave, setFinenessModulusToSave] = useState('');
  const [packagingToSave, setPackagingToSave] = useState('');

  const [comboBoxOptionsToSands, setcomboBoxOptionsToSands] = useState([]);
  const [comboBoxOptionsToGravels, setcomboBoxOptionsToGravels] = useState([]);
  const [comboBoxOptionsToWaters, setcomboBoxOptionsToWaters] = useState([]);
  const [comboBoxOptionsToCements, setcomboBoxOptionsToCements] = useState([]);
  const [comboBoxOptionsToWaterReducingAdditives, setcomboBoxOptionsToWaterReducingAdditives] = useState([]);
  const [comboBoxOptionsToAdditives, setcomboBoxOptionsToAdditives] = useState([]);

  const [gravelToSave, setGravelToSave] = useState('');
  const [percentageFromGravelToSave, setPercentageFromGravelToSave] = useState('');
  const [humidityFromGravelToSave, setHumidityFromGravelToSave] = useState('');
  const [gravelsAndPercentagesToSave, setGravelsAndPercentagesToSave] = useState([]);

  const [sandToSave, setSandToSave] = useState('');
  const [percentageFromSandToSave, setPercentageFromSandToSave] = useState('');
  const [humidityFromSandToSave, setHumidityFromSandToSave] = useState('');
  const [sandsAndPercentagesToSave, setSandsAndPercentagesToSave] = useState([]);

  const [showModalToGenerateReceipt, setShowModalToGenerateReceipt] = useState(false);
  const [receiptName, setReceiptName] = useState('');
  const [receiptResistence, setReceiptResistence] = useState('');
  const [receiptSlump, setReceiptSlump] = useState('');
  const [receiptIncorporatedAir, setReceiptIncorporatedAir] = useState('');
  const [generatedReceipt, setGeneratedReceipt] = useState(null);

  const [accumulatedRetainedSieveFromGravels, setAccumulatedRetainedSieveFromGravels] = useState([]);
  const [accumulatedRetainedSieveFromSands, setAccumulatedRetainedSieveFromSands] = useState([]);
  const { getThemeColor } = useAppContext();

  const graphicDataFromGravel = {
    labels: accumulatedRetainedSieveFromGravels.sieves,
    datasets: [
      {
        type: 'line',
        label: 'Retido Acumulado',
        borderColor: getThemeColor('primary'),
        borderWidth: 2,
        fill: false,
        data: accumulatedRetainedSieveFromGravels.accumulatedValues,
        tension: 0.3
      }
    ]
  };

  const graphicDataFromSand = {
    labels: accumulatedRetainedSieveFromSands.sieves,
    datasets: [
      {
        type: 'line',
        label: 'Retido Acumulado',
        borderColor: getThemeColor('primary'),
        borderWidth: 2,
        fill: false,
        data: accumulatedRetainedSieveFromSands.accumulatedValues,
        tension: 0.3
      }
    ]
  };

  const options = {
    plugins: {
      tooltip: chartJsDefaultTooltip(getThemeColor),
      legend: {
        labels: {
          color: getThemeColor('gray-500')
        }
      }
    },
    scales: {
      x: {
        ticks: {
          color: getThemeColor('gray-500')
        },
        grid: {
          color: getThemeColor('gray-300'),
          drawBorder: false
        }
      },
      y: {
        reverse: true,
        ticks: {
          color: getThemeColor('gray-500')
        },
        grid: {
          color: getThemeColor('gray-300'),
          drawBorder: false,
        }
      }
    }
  };

  function sanitizeNumericInput(value) {
    return value.replace(/[^0-9.,]/g, '');
  }

  useEffect(() => {
    getMaterials();

    if (dosageStudyId) {
      openDosageStudy(dosageStudyId);
    }
  }, []);

  function getMaterials() {
    getGravels();
    getSands();
    getAllWatersToComboBox();
    getAllCementsToComboBox();
    getAllWaterReducingAdditivesToComboBox();
    getAllAdditivesToComboBox();
  }

  function openDosageStudy(dosageStudyId) {
    setLoading(true);
    api.get('/dosage-study/' + dosageStudyId, {
      headers: {
        Authorization: sessionStorage.getItem('token')
      }
    }).then((returnFromApi) => {
      const dosageStudy = returnFromApi.data;
      setNameToSave(dosageStudy.name);
      setCodeToSave(dosageStudy.code || '');
      setMortarPercentageToSave(dosageStudy.mortarPercentage);
      setIncorporatedAirToSave(dosageStudy.percentageOfincorporatedAir);
      setCementConsumptionPerCubicMeterToSave(dosageStudy.cementConsumptionPerCubicMeter);
      setWaterConsumptionPerCubicMeterToSave(dosageStudy.waterConsumptionPerCubicMeter);
      setCementToSave({
        value: dosageStudy.cement.name,
        label: dosageStudy.cement.materialId
      });
      setWaterToSave({
        value: dosageStudy.water.name,
        label: dosageStudy.water.materialId
      });
      setWaterReducingAdditiveToSave({
        value: dosageStudy.waterReducingAdditiveWithUsage.waterReducingAdditiveId,
        label: dosageStudy.waterReducingAdditiveWithUsage.name
      });
      setWaterReducingAdditivePercentageToSave(dosageStudy.waterReducingAdditiveWithUsage.percentageOfUsage);
      setGravelsAndPercentagesToSave(
        dosageStudy.gravelsAndPercentages.map((gravel) => ({
          value: gravel.gravelId,
          label: gravel.name,
          percentage: gravel.percentage,
          humidity: gravel.humidityPercentage
        }))
      );
      setAverageDiameterToSave(dosageStudy.averageDiameter);
      setMaxDiameterToSave(dosageStudy.maxDiameter);
      setSandsAndPercentagesToSave(
        dosageStudy.sandsAndPercentages.map((sand) => ({
          value: sand.sandId,
          label: sand.name,
          percentage: sand.percentage,
          humidity: sand.humidityPercentage
        }))
      );
      setFinenessModulusToSave(dosageStudy.finenessModulus);
      setPackagingToSave(dosageStudy.packagingValue);
      setConcreteVolumeToSave(dosageStudy.concreteVolume);
      setObservationToSave(dosageStudy.observation);
      setAccumulatedRetainedSieveFromGravels(dosageStudy.accumulatedRetainedSieveFromGravels);
      setAccumulatedRetainedSieveFromSands(dosageStudy.accumulatedRetainedSieveFromSands);
      setLoading(false);
    }).catch((err) => {
      setLoading(false);
      console.error('ops! ocorreu um erro' + err);
    });
  }

  function getGravels() {
    setLoading(true);
    api
      .get('/materials/gravels/combobox', {
        headers: {
          Authorization: sessionStorage.getItem('token')
        }
      })
      .then((returnFromApi) => {
        const options = returnFromApi.data.map((material) => ({
          value: material.materialId,
          label: material.name
        }));
        setcomboBoxOptionsToGravels(options);

        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        console.error('ops! ocorreu um erro' + err);
      });
  }

  function getSands() {
    setLoading(true);
    api
      .get('/materials/sands/combobox', {
        headers: {
          Authorization: sessionStorage.getItem('token')
        }
      })
      .then((returnFromApi) => {
        const options = returnFromApi.data.map((material) => ({
          value: material.materialId,
          label: material.name
        }));
        setcomboBoxOptionsToSands(options);

        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        console.error('ops! ocorreu um erro' + err);
      });
  }

  function getAllWatersToComboBox() {
    setLoading(true);
    api
      .get('/materials/waters/combobox', {
        headers: {
          Authorization: sessionStorage.getItem('token')
        }
      })
      .then((returnFromApi) => {
        const options = returnFromApi.data.map((material) => ({
          value: material.materialId,
          label: material.name
        }));
        setcomboBoxOptionsToWaters(options);

        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        console.error('ops! ocorreu um erro' + err);
      });
  }

  function getAllCementsToComboBox() {
    setLoading(true);
    api
      .get('/materials/cements/combobox', {
        headers: {
          Authorization: sessionStorage.getItem('token')
        }
      })
      .then((returnFromApi) => {
        const options = returnFromApi.data.map((material) => ({
          value: material.materialId,
          label: material.name
        }));
        setcomboBoxOptionsToCements(options);

        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        console.error('ops! ocorreu um erro' + err);
      });
  }

  function getAllWaterReducingAdditivesToComboBox() {
    setLoading(true);
    api
      .get('/materials/water-reducing-additives/combobox', {
        headers: {
          Authorization: sessionStorage.getItem('token')
        }
      })
      .then((returnFromApi) => {
        const options = returnFromApi.data.map((material) => ({
          value: material.materialId,
          label: material.name
        }));
        setcomboBoxOptionsToWaterReducingAdditives(options);

        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        console.error('ops! ocorreu um erro' + err);
      });
  }

  function getAllAdditivesToComboBox() {
    setLoading(true);
    api
      .get('/materials/others-additives/combobox', {
        headers: {
          Authorization: sessionStorage.getItem('token')
        }
      })
      .then((returnFromApi) => {
        const options = returnFromApi.data.map((material) => ({
          value: material.materialId,
          label: material.name
        }));
        setcomboBoxOptionsToAdditives(options);

        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        console.error('ops! ocorreu um erro' + err);
      });
  }

  function addGravel() {
    const gravel = {
      value: gravelToSave.value,
      label: gravelToSave.label,
      percentage: percentageFromGravelToSave,
      humidity: humidityFromGravelToSave
    };

    gravelsAndPercentagesToSave.push(gravel);

    calculateAverageDiameter();
    calculateMaximumDiameter();
    generateGraphicToAccumulatedRetainedFromGravel();

    setGravelToSave('');
    setPercentageFromGravelToSave('');
    setHumidityFromGravelToSave('');
  }

  function calculateMaximumDiameter() {
    setLoading(true);
    const input = gravelsAndPercentagesToSave.map((item) => ({
      percentageOfUsage: item.percentage,
      materialId: item.value
    }));

    api
      .post('/dosage-study/maximum-diameter/calculate', input, {
        headers: {
          Authorization: sessionStorage.getItem('token')
        }
      })
      .then((returnFromApi) => {
        setMaxDiameterToSave(returnFromApi.data);
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        console.error('ops! ocorreu um erro: ' + err);
      });
  }

  function calculateAverageDiameter() {
    setLoading(true);
    const input = gravelsAndPercentagesToSave.map((item) => ({
      percentageOfUsage: item.percentage,
      materialId: item.value
    }));

    api
      .post('/dosage-study/average-diameter/calculate', input, {
        headers: {
          Authorization: sessionStorage.getItem('token')
        }
      })
      .then((returnFromApi) => {
        setAverageDiameterToSave(returnFromApi.data);
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        console.error('ops! ocorreu um erro: ' + err);
      });
  }

  function generateGraphicToAccumulatedRetainedFromGravel() {
    const input = gravelsAndPercentagesToSave.map((item) => ({
      percentageOfUsage: item.percentage,
      materialId: item.value
    }));

    api.post('/dosage-calculation/accumulated-retained/graphic', input, {
      headers: {
        Authorization: sessionStorage.getItem('token')
      }
    }).then((returnFromApi) => {
      setAccumulatedRetainedSieveFromGravels(returnFromApi.data);
    }).catch((err) => {
      console.error('ops! ocorreu um erro: ' + err);
    });
  }

  function generateGraphicToAccumulatedRetainedFromSand() {
    const input = sandsAndPercentagesToSave.map((item) => ({
      percentageOfUsage: item.percentage,
      materialId: item.value
    }));

    api.post('/dosage-calculation/accumulated-retained/graphic', input, {
      headers: {
        Authorization: sessionStorage.getItem('token')
      }
    }).then((returnFromApi) => {
      setAccumulatedRetainedSieveFromSands(returnFromApi.data);
    }).catch((err) => {
      console.error('ops! ocorreu um erro: ' + err);
    });
  }

  function calculateFinenessModulus() {
    setLoading(true);
    const input = sandsAndPercentagesToSave.map((item) => ({
      percentageOfUsage: item.percentage,
      materialId: item.value
    }));

    api
      .post('/dosage-study/fineness-modulus/calculate', input, {
        headers: {
          Authorization: sessionStorage.getItem('token')
        }
      })
      .then((returnFromApi) => {
        setFinenessModulusToSave(returnFromApi.data);
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        console.error('ops! ocorreu um erro: ' + err);
      });
  }

  function calculatePackaging() {
    setLoading(true);
    const gravels = gravelsAndPercentagesToSave.map((item) => ({
      gravelId: item.value,
      percentage: item.percentage,
      humidityPercentage: item.humidity
    }));
    const sands = sandsAndPercentagesToSave.map((item) => ({
      sandId: item.value,
      percentage: item.percentage,
      humidityPercentage: item.humidity
    }));

    const input = {
      "cementId": cementToSave.value,
      "waterId": waterToSave.value,
      "waterReducingAdditiveId": waterReducingAdditiveToSave.value,
      "mortarPercentage": mortarPercentageToSave,
      "percentageOfIncorporatedAir": incorporatedAirToSave,
      "additivePercentage": waterReducingAdditivePercentageToSave,
      "cementConsumptionPerCubicMeter": cementConsumptionPerCubicMeterToSave,
      "waterConsumptionPerCubicMeter": waterConsumptionPerCubicMeterToSave,
      "sandsAndPercentagesDTO": sands,
      "gravelAndPercentagesDTO": gravels
    }

    console.log(input);

    api.post('/dosage-study/packaging/calculate', input, {
      headers: {
        Authorization: sessionStorage.getItem('token')
      }
    }).then((returnFromApi) => {
      setPackagingToSave(returnFromApi.data);
      setLoading(false);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }).catch((returnFromApi) => {
      setLoading(false);
      setShowErrorMessage(true);
      setErrorMessage(returnFromApi.response.data);
      window.scrollTo({ top: 0, behavior: "smooth" });
      console.error('ops! ocorreu um erro: ' + returnFromApi);
    });
  }

  function deleteGravelFromList(gravel) {
    const gravelIndex = gravelsAndPercentagesToSave.indexOf(gravel);

    if (gravelIndex > -1) {
      gravelsAndPercentagesToSave.splice(gravelIndex, 1);
    }
    calculateAverageDiameter();
    calculateMaximumDiameter();
    generateGraphicToAccumulatedRetainedFromGravel();
  }

  function addSand() {
    const sand = {
      value: sandToSave.value,
      label: sandToSave.label,
      percentage: percentageFromSandToSave,
      humidity: humidityFromSandToSave
    };

    sandsAndPercentagesToSave.push(sand);

    calculateFinenessModulus();
    calculatePackaging();
    generateGraphicToAccumulatedRetainedFromSand();

    setSandToSave('');
    setPercentageFromSandToSave('');
    setHumidityFromSandToSave('');
  }

  function deleteSandFromList(sand) {
    const sandIndex = sandsAndPercentagesToSave.indexOf(sand);

    if (sandIndex > -1) {
      sandsAndPercentagesToSave.splice(sandIndex, 1);
    }

    calculateFinenessModulus();
    calculatePackaging();
    generateGraphicToAccumulatedRetainedFromSand();
  }

  function addOtherAdditive() {
    if (!otherAdditivesToSave || !otherAdditivesPercentageToSave) {
      setShowErrorMessage(true);
      setErrorMessage('Por favor, selecione um aditivo e informe a porcentagem.');
      return;
    }

    const additive = {
      additiveToSave: otherAdditivesToSave,
      percentageFromAdditiveToSave: otherAdditivesPercentageToSave
    };

    otherAdditivesAndPercentagesToSave.push(additive);

    setOtherAdditivesToSave('');
    setOtherAdditivesPercentageToSave('');
  }

  function deleteOtherAdditiveFromList(additive) {
    const additiveIndex = otherAdditivesAndPercentagesToSave.indexOf(additive);

    if (additiveIndex > -1) {
      otherAdditivesAndPercentagesToSave.splice(additiveIndex, 1);
    }
  }

  const saveDosageEstudy = (event) => {
    event.preventDefault();
    setLoading(true);

    const waterReducingAdditiveWithUsage = {
      waterReducingAdditiveId: waterReducingAdditiveToSave.value,
      percentageOfUsage: waterReducingAdditivePercentageToSave
    };
    const input = {
      dosageStudyId: dosageStudyId,
      name: nameToSave,
      code: codeToSave,
      mortarPercentage: mortarPercentageToSave,
      percentageOfincorporatedAir: incorporatedAirToSave,
      cementConsumptionPerCubicMeter: cementConsumptionPerCubicMeterToSave,
      waterConsumptionPerCubicMeter: waterConsumptionPerCubicMeterToSave,
      cementId: cementToSave.value,
      waterId: waterToSave.value,
      waterReducingAdditiveWithUsage: waterReducingAdditiveWithUsage,
      otherAdditives: otherAdditivesToSave?.value || '',
      otherAdditivesPercentage: otherAdditivesPercentageToSave,
      otherAdditivesAndPercentages: otherAdditivesAndPercentagesToSave.map((item) => ({
        additiveId: item.additiveToSave.value,
        percentage: item.percentageFromAdditiveToSave
      })),
      gravelsAndPercentages: gravelsAndPercentagesToSave.map((item) => ({
        gravelId: item.value,
        percentage: item.percentage,
        humidityPercentage: item.humidity
      })),
      sandsAndPercentages: sandsAndPercentagesToSave.map((item) => ({
        sandId: item.value,
        percentage: item.percentage,
        humidityPercentage: item.humidity
      })),
      averageDiameter: averageDiameterToSave,
      maxDiameter: maxDiameterToSave,
      finenessModulus: finenessModulusToSave,
      packagingValue: packagingToSave,
      concreteVolume: concreteVolumeToSave,
      observation: observationToSave
    };
    setLoading(false);

    if (dosageStudyId) {
      api.put('/dosage-study', input, {
        headers: {
          Authorization: sessionStorage.getItem('token')
        }
      }).then((returnFromApi) => {
        setLoading(false);
        setShowSuccessMessage(true);
        console.log(returnFromApi.data);
        // setSuccessMessage(returnFromApi.data);
      }).catch((error) => {
        setShowErrorMessage(true);
        setErrorMessage(error.response.data);
        setLoading(false);
      });
    } else {
      api.post('/dosage-study', input, {
        headers: {
          Authorization: sessionStorage.getItem('token')
        }
      }).then((returnFromApi) => {
        setLoading(false);
        setShowSuccessMessage(true);
        setSuccessMessage(returnFromApi.data.message);
        setDosageStudyId(returnFromApi.data.content);
      }).catch((error) => {
        setShowErrorMessage(true);
        setErrorMessage(error.response.data);
        setLoading(false);
      });
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const generateReceipt = (event) => {
    event.preventDefault();
    setLoading(true);

    const input = {
      dosageStudyId: dosageStudyId,
      name: receiptName,
      resistence: receiptResistence,
      percentageOfIncorporatedAir: receiptIncorporatedAir,
      slump: receiptSlump
    };
    api
      .post('/dosage-study/receipt', input, {
        headers: {
          Authorization: sessionStorage.getItem('token')
        }
      })
      .then((returnFromApi) => {
        openReceipt();
        setSuccessMessage(returnFromApi.data);
        setShowSuccessMessage(true);
        setLoading(false);
      })
      .catch((error) => {
        setShowErrorMessage(true);
        setErrorMessage(error.response.data);
        setLoading(false);
      });
  };

  function openModalToGenerateReceipt() {
    openReceipt();
    setShowModalToGenerateReceipt(true);
  }

  function openReceipt() {
    setLoading(true);

    api
      .get(`/dosage-study/${dosageStudyId}/receipts`, {
        headers: {
          Authorization: sessionStorage.getItem('token')
        }
      }).then((returnFromApi) => {
        const receipt = returnFromApi.data;
        console.log(receipt);
        setGeneratedReceipt(receipt);
        setReceiptName(receipt.name);
        setReceiptResistence(receipt.formattedConcreteResistence);
        setReceiptSlump(receipt.formattedSlump);
        setReceiptIncorporatedAir(receipt.formattedPercentageOfIncorporatedAir);
        setLoading(false);
      }).catch((error) => {
        setLoading(false);
      });
  }

  function cleanFields() {
    setNameToSave('');
    setCodeToSave('');
    setMortarPercentageToSave('');
    setIncorporatedAirToSave('');
    setCementConsumptionPerCubicMeterToSave('');
    setWaterConsumptionPerCubicMeterToSave('');
    setCementToSave('');
    setWaterToSave('');
    setWaterReducingAdditiveToSave('');
    setWaterReducingAdditivePercentageToSave('');
    setOtherAdditivesToSave('');
    setOtherAdditivesPercentageToSave('');
    setOtherAdditivesAndPercentagesToSave([]);
    setGravelsAndPercentagesToSave([]);
    setSandsAndPercentagesToSave([]);
    setHumidityFromGravelToSave('');
    setHumidityFromSandToSave('');
    setAverageDiameterToSave('');
    setMaxDiameterToSave('');
    setFinenessModulusToSave('');
    setPackagingToSave('');
    setConcreteVolumeToSave('');
    setObservationToSave('');
  }

  function returnToDosageEstudyList() {
    navigate('/dosage-study');
  }

  function openModalToViewConcreteDosage(concreteDosage) {
    setLoading(true);
    api
      .get('/concrete-dosage/view/' + concreteDosage.concreteDosageId, {
        headers: {
          Authorization: sessionStorage.getItem('token')
        }
      })
      .then((returnFromApi) => {
        console.log(returnFromApi.data);
        setRegisteredConcreteDosage(returnFromApi.data);
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        console.error('ops! ocorreu um erro' + err);
      });

    setShowRegisteredConcreteDosageModal(true);
  }

  function openReceipts() {
    navigate(`/dosage-study/receipts?dosage-study-id=${dosageStudyId}`);
  }

  return (
    <Card className="bg-transparent-50 overflow-hidden">
      <Authenticator />
      <Card.Header className="position-relative">
        <div className="row flex-between-center">
          <div className="col col-lg-8">
            <h5 className="mb-0 texto-negrito">Estudo de Dosagem</h5>
          </div>
        </div>

        {showSuccessMessage && <SuccessMessage message={successMessage} setShow={setShowSuccessMessage} />}
        {showErrorMessage && <ErrorMessage message={errorMessage} setShow={setShowErrorMessage} />}
        <Loading isToHidden={isLoading == false} />
      </Card.Header>
      <Card.Body>
        <Form onSubmit={saveDosageEstudy} className={`form-container ${isLoading ? 'loading' : 'mt-3'}`}>
          {/* Estudo de dosagem Section */}
          <div className="mb-4">
            <h4 className="mb-3 text-dark fw-bold">Estudo de dosagem</h4>
            <Row>
              <Col xs={12} md={6}>
                <Form.Group className="mb-3" controlId="titleInput">
                  <Form.Label className="fs-9">Nome do estudo</Form.Label>
                  <Form.Control type="text" value={nameToSave} onChange={(e) => setNameToSave(e.target.value)} />
                </Form.Group>
              </Col>
              <Col xs={12} md={6}>
                <Form.Group className="mb-3" controlId="titleInput">
                  <Form.Label className="fs-9">Código</Form.Label>
                  <Form.Control type="text" value={codeToSave} onChange={(e) => setCodeToSave(e.target.value)} />
                </Form.Group>
              </Col>
            </Row>
          </div>

          {/* Parâmetros do concreto Section */}
          <div className="mb-4">
            <h4 className="mb-3 text-dark fw-bold">Parâmetros do concreto</h4>
            <Row>
              <Col xs={12} md={6}>
                <Form.Group className="mb-3" controlId="titleInput">
                  <Form.Label className="fs-9">Teor de argamassa (%)</Form.Label>
                  <Form.Control
                    type="text"
                    value={mortarPercentageToSave}
                    onChange={(e) => setMortarPercentageToSave(sanitizeNumericInput(e.target.value))}
                  />
                </Form.Group>
              </Col>
              <Col xs={12} md={6}>
                <Form.Group className="mb-3" controlId="titleInput">
                  <Form.Label className="fs-9">Ar incorporado (%)</Form.Label>
                  <Form.Control
                    type="text"
                    value={incorporatedAirToSave}
                    onChange={(e) => setIncorporatedAirToSave(sanitizeNumericInput(e.target.value))}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col xs={12} md={6}>
                <Form.Group className="mb-3" controlId="titleInput">
                  <Form.Label className="fs-9">Consumo de cimento por m³</Form.Label>
                  <Form.Control
                    type="text"
                    value={cementConsumptionPerCubicMeterToSave}
                    onChange={(e) => setCementConsumptionPerCubicMeterToSave(sanitizeNumericInput(e.target.value))}
                  />
                </Form.Group>
              </Col>
              <Col xs={12} md={6}>
                <Form.Group className="mb-3" controlId="titleInput">
                  <Form.Label className="fs-9">Cimento</Form.Label>
                  <Select
                    closeMenuOnSelect={false}
                    options={comboBoxOptionsToCements}
                    placeholder="Selecione..."
                    classNamePrefix="react-select"
                    value={cementToSave}
                    onChange={(value) => setCementToSave(value)}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col xs={12} md={6}>
                <Form.Group className="mb-3" controlId="titleInput">
                  <Form.Label className="fs-9">Consumo de água por m³</Form.Label>
                  <Form.Control
                    type="text"
                    value={waterConsumptionPerCubicMeterToSave}
                    onChange={(e) => setWaterConsumptionPerCubicMeterToSave(sanitizeNumericInput(e.target.value))}
                  />
                </Form.Group>
              </Col>
              <Col xs={12} md={6}>
                <Form.Group className="mb-3" controlId="titleInput">
                  <Form.Label className="fs-9">Água</Form.Label>
                  <Select
                    closeMenuOnSelect={false}
                    options={comboBoxOptionsToWaters}
                    placeholder="Selecione..."
                    classNamePrefix="react-select"
                    value={waterToSave}
                    onChange={(value) => setWaterToSave(value)}
                  />
                </Form.Group>
              </Col>
            </Row>
          </div>

          {/* Aditivos Section */}
          <div className="mb-4">
            <h4 className="mb-3 text-dark fw-bold">Aditivos</h4>
            <Row>
              <Col xs={12} md={6}>
                <Form.Group className="mb-3" controlId="titleInput">
                  <Form.Label className="fs-9">Aditivo redutor de água</Form.Label>
                  <Select
                    closeMenuOnSelect={false}
                    options={comboBoxOptionsToWaterReducingAdditives}
                    placeholder="Selecione..."
                    classNamePrefix="react-select"
                    value={waterReducingAdditiveToSave}
                    onChange={(value) => setWaterReducingAdditiveToSave(value)}
                  />
                </Form.Group>
              </Col>
              <Col xs={12} md={4}>
                <Form.Group className="mb-3" controlId="titleInput">
                  <Form.Label className="fs-9">Porcentagem</Form.Label>
                  <Form.Control
                    type="text"
                    value={waterReducingAdditivePercentageToSave}
                    onChange={(e) => setWaterReducingAdditivePercentageToSave(sanitizeNumericInput(e.target.value))}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col xs={12}>
                <Table responsive hidden={otherAdditivesAndPercentagesToSave.length === 0}>
                  <thead>
                    <tr>
                      <th scope="col">Outros Aditivos</th>
                      <th scope="col">Porcentagem</th>
                      <th scope="col"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {otherAdditivesAndPercentagesToSave.map((additive, index) => {
                      return (
                        <tr key={index}>
                          <td>{additive.additiveToSave.label}</td>
                          <td>{additive.percentageFromAdditiveToSave}%</td>
                          <td className="text-end">
                            <Dropdown className="d-inline-block ms-2">
                              <Dropdown.Toggle variant="falcon-default">Ações</Dropdown.Toggle>
                              <Dropdown.Menu>
                                <Dropdown.Item
                                  as={Link}
                                  onClick={() => deleteOtherAdditiveFromList(additive)}
                                  className="text-danger"
                                >
                                  Excluir
                                </Dropdown.Item>
                              </Dropdown.Menu>
                            </Dropdown>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </Col>
            </Row>
          </div>

          {/* Britas e porcentagens de uso Section */}
          <div className="mb-4">
            <h4 className="mb-3 text-dark fw-bold">Britas e porcentagens de uso</h4>
            <Row>
              <Col xs={12} md={4}>
                <Form.Group className="mb-3" controlId="titleInput">
                  <Form.Label className="fs-9">Brita</Form.Label>
                  <Select
                    closeMenuOnSelect={false}
                    options={comboBoxOptionsToGravels}
                    placeholder="Selecione..."
                    classNamePrefix="react-select"
                    value={gravelToSave}
                    onChange={(value) => setGravelToSave(value)}
                  />
                </Form.Group>
              </Col>
              <Col xs={12} md={3}>
                <Form.Group className="mb-3" controlId="titleInput">
                  <Form.Label className="fs-9">Porcentagem</Form.Label>
                  <Form.Control
                    type="text"
                    value={percentageFromGravelToSave}
                    onChange={(e) => setPercentageFromGravelToSave(sanitizeNumericInput(e.target.value))}
                  />
                </Form.Group>
              </Col>
              <Col xs={12} md={3}>
                <Form.Group className="mb-3" controlId="titleInput">
                  <Form.Label className="fs-9">Umidade (%)</Form.Label>
                  <Form.Control
                    type="text"
                    value={humidityFromGravelToSave}
                    onChange={(e) => setHumidityFromGravelToSave(sanitizeNumericInput(e.target.value))}
                  />
                </Form.Group>
              </Col>
              <Col xs={12} md={2}>
                <Button className="mt-4" onClick={() => addGravel()}>
                  Adicionar
                </Button>
              </Col>
            </Row>
            <Row>
              <Col xs={12} md={6}>
                <Form.Group className="mb-3" controlId="titleInput">
                  <Form.Label className="fs-9">Diâmetro médio (mm)</Form.Label>
                  <Form.Control
                    disabled={true}
                    type="text"
                    value={averageDiameterToSave}
                    onChange={(e) => setAverageDiameterToSave(sanitizeNumericInput(e.target.value))}
                  />
                </Form.Group>
              </Col>
              <Col xs={12} md={6}>
                <Form.Group className="mb-3" controlId="titleInput">
                  <Form.Label className="fs-9">Diâmetro máximo (mm)</Form.Label>
                  <Form.Control
                    disabled={true}
                    type="text"
                    value={maxDiameterToSave}
                    onChange={(e) => setMaxDiameterToSave(sanitizeNumericInput(e.target.value))}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              {/* TABELA */}
              <Col md={7}>
                <Table
                  responsive
                  hidden={gravelsAndPercentagesToSave.length === 0}
                  className="mb-0"
                >
                  <thead>
                    <tr>
                      <th scope="col">Brita</th>
                      <th scope="col">Porcentagem</th>
                      <th scope="col">Umidade (%)</th>
                      <th scope="col"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {gravelsAndPercentagesToSave.map((gravel) => (
                      <tr key={gravel.value}>
                        <td>{gravel.label}</td>
                        <td>{gravel.percentage}%</td>
                        <td>{gravel.humidity}%</td>
                        <td>
                          <Dropdown className="d-inline-block ms-2">
                            <Dropdown.Toggle variant="falcon-default">
                              Ações
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                              <Dropdown.Item
                                as={Link}
                                onClick={() => deleteGravelFromList(gravel)}
                                className="text-danger"
                              >
                                Excluir
                              </Dropdown.Item>
                            </Dropdown.Menu>
                          </Dropdown>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Col>

              {/* GRÁFICO */}
              <Col md={5}>
                <div style={{
                  width: "100%",
                  height: "350px"
                }}>
                  <Line data={graphicDataFromGravel} options={options} />
                </div>
              </Col>
            </Row>
          </div>

          {/* Areias e porcentagens de uso Section */}
          <div className="mb-4">
            <h4 className="mb-3 text-dark fw-bold">Areias e porcentagens de uso</h4>
            <Row>
              <Col xs={12} md={4}>
                <Form.Group className="mb-3" controlId="titleInput">
                  <Form.Label className="fs-9">Areia</Form.Label>
                  <Select
                    closeMenuOnSelect={false}
                    options={comboBoxOptionsToSands}
                    placeholder="Selecione..."
                    classNamePrefix="react-select"
                    value={sandToSave}
                    onChange={(value) => setSandToSave(value)}
                  />
                </Form.Group>
              </Col>
              <Col xs={12} md={3}>
                <Form.Group className="mb-3" controlId="titleInput">
                  <Form.Label className="fs-9">Porcentagem</Form.Label>
                  <Form.Control
                    type="text"
                    value={percentageFromSandToSave}
                    onChange={(e) => setPercentageFromSandToSave(sanitizeNumericInput(e.target.value))}
                  />
                </Form.Group>
              </Col>
              <Col xs={12} md={3}>
                <Form.Group className="mb-3" controlId="titleInput">
                  <Form.Label className="fs-9">Umidade (%)</Form.Label>
                  <Form.Control
                    type="text"
                    value={humidityFromSandToSave}
                    onChange={(e) => setHumidityFromSandToSave(sanitizeNumericInput(e.target.value))}
                  />
                </Form.Group>
              </Col>
              <Col xs={12} md={2}>
                <Button className="mt-4" onClick={() => addSand()}>
                  Adicionar
                </Button>
              </Col>
            </Row>
            <Row>
              <Col xs={12} md={6}>
                <Form.Group className="mb-3" controlId="titleInput">
                  <Form.Label className="fs-9">Módulo de Finura</Form.Label>
                  <Form.Control
                    disabled={true}
                    type="text"
                    value={finenessModulusToSave}
                    onChange={(e) => setFinenessModulusToSave(sanitizeNumericInput(e.target.value))}
                  />
                </Form.Group>
              </Col>
              <Col xs={12} md={6}>
                <Form.Group className="mb-3" controlId="titleInput">
                  <Form.Label className="fs-9">Empacotamento</Form.Label>
                  <Form.Control
                    disabled={true}
                    type="text"
                    value={packagingToSave}
                    onChange={(e) => setPackagingToSave(sanitizeNumericInput(e.target.value))}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={7}>
                <Table responsive hidden={sandsAndPercentagesToSave.length === 0}>
                  <thead>
                    <tr>
                      <th scope="col">Areia</th>
                      <th scope="col">Porcentagem</th>
                      <th scope="col">Umidade (%)</th>
                      <th scope="col"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {sandsAndPercentagesToSave.map((sand) => {
                      return (
                        <tr key={sand.value}>
                          <td>{sand.label}</td>
                          <td>{sand.percentage}%</td>
                          <td>{sand.humidity}%</td>
                          <td>
                            <Dropdown className="d-inline-block ms-2">
                              <Dropdown.Toggle variant="falcon-default">Ações</Dropdown.Toggle>
                              <Dropdown.Menu>
                                <Dropdown.Item
                                  as={Link}
                                  onClick={() => deleteSandFromList(sand)}
                                  className="text-danger"
                                >
                                  Excluir
                                </Dropdown.Item>
                              </Dropdown.Menu>
                            </Dropdown>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </Col>
              {/* GRÁFICO */}
              <Col md={5}>
                <div style={{
                  width: "100%",
                  height: "350px"
                }}>
                  <Line data={graphicDataFromSand} options={options} />
                </div>
              </Col>

            </Row>
          </div>

          {/* Volume de Concreto Section */}
          <div className="mb-4">
            <h4 className="mb-3 text-dark fw-bold">Volume de Concreto</h4>
            <Row>
              <Col xs={12} md={6}>
                <Form.Group className="mb-3" controlId="titleInput">
                  <Form.Label className="fs-9">Volume de concreto (m³)</Form.Label>
                  <Form.Control
                    type="text"
                    value={concreteVolumeToSave}
                    onChange={(e) => setConcreteVolumeToSave(sanitizeNumericInput(e.target.value))}
                  />
                </Form.Group>
              </Col>
            </Row>
          </div>

          <Row>
            <Col xs={12} md={12}>
              <Form.Group className="mb-3" controlId="titleInput">
                <Form.Label className="fs-9">Observações</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={observationToSave}
                  onChange={(e) => setObservationToSave(e.target.value)}
                />
              </Form.Group>
            </Col>
          </Row>

          <div className="d-flex justify-content-end mt-4">
            <Button variant="falcon-primary" className="me-3" onClick={() => openModalToGenerateReceipt()}>
              Gerar Receita
            </Button>
            <Button variant="falcon-primary" className="me-3" onClick={() => returnToDosageEstudyList()}>
              Voltar
            </Button>
            <Button type="submit">Salvar</Button>
          </div>
        </Form>

        <Modal
          show={showModalToGenerateReceipt}
          backdrop="static"
          keyboard={false}
          onHide={() => setShowModalToGenerateReceipt(false)}
          size="lg"
          aria-labelledby="contained-modal-title-vcenter"
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title id="contained-modal-title-vcenter">Receita</Modal.Title>
          </Modal.Header>
          {showSuccessMessage && <SuccessMessage message={successMessage} setShow={setShowSuccessMessage} />}
          {showErrorMessage && <ErrorMessage message={errorMessage} setShow={setShowErrorMessage} />}
          <Loading isToHidden={isLoading == false} />

          <Modal.Body>
            <Form onSubmit={generateReceipt} className={`form-container ${isLoading ? 'loading' : 'mt-3'}`}>
              <Row>
                <Col xs={6}>
                  <Form.Group className="mb-3" controlId="titleInput">
                    <Form.Label className="fs-9">Nome</Form.Label>
                    <Form.Control type="text" value={receiptName} onChange={(e) => setReceiptName(e.target.value)} />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col xs={3}>
                  <Form.Group className="mb-3" controlId="titleInput">
                    <Form.Label className="fs-9">Resistência</Form.Label>
                    <Form.Control
                      type="text"
                      value={receiptResistence}
                      onChange={(e) => setReceiptResistence(e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col xs={3}>
                  <Form.Group className="mb-3" controlId="titleInput">
                    <Form.Label className="fs-9">Slump</Form.Label>
                    <Form.Control type="text" value={receiptSlump} onChange={(e) => setReceiptSlump(e.target.value)} />
                  </Form.Group>
                </Col>
                <Col xs={3}>
                  <Form.Group className="mb-3" controlId="titleInput">
                    <Form.Label className="fs-9">Ar Incorporado</Form.Label>
                    <Form.Control
                      type="text"
                      value={receiptIncorporatedAir}
                      onChange={(e) => setReceiptIncorporatedAir(e.target.value)}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <hr color="#424242" size="2"></hr>
              <Row>
                <Col xs={5}>Massa Específica (kg/m³):</Col>
                <Col>{generatedReceipt?.formattedSpecificMass}</Col>
              </Row>
              <Row>
                <Col xs={5}>Massa de Cimento (kg):</Col>
                <Col>{generatedReceipt?.formattedCementMassMultipliedByConcreteVolume}</Col>
              </Row>
              <Row>
                <Col xs={5}>Massa de Água (kg):</Col>
                <Col>{generatedReceipt?.formattedWaterMassWithHumidityCorrectionMultipliedByConcreteVolume}</Col>
              </Row>
              <Row>
                <Col xs={5}>Massa de Aditivo (kg):</Col>
                <Col>{generatedReceipt?.formattedAdditiveMassMultipliedByConcreteVolume}</Col>
              </Row>
              <Row>
                <Col xs={5}>Volume de Concreto (m³):</Col>
                <Col>{generatedReceipt?.formattedConcreteVolume}</Col>
              </Row>
              <hr color="#424242" size="2"></hr>
              <h4>Britas com Umidade Corrigida</h4>
              {generatedReceipt?.gravelsMassWithHumidityCorrectionMultipliedByConcreteVolume?.map((gravel) => {
                return (
                  <Row>
                    <Col xs={5}>{gravel.formattedGravelName}</Col>
                    <Col>{gravel.formattedQuantity}</Col>
                  </Row>
                );
              })}
              <hr color="#424242" size="2"></hr>
              <h4>Areias com Umidade Corrigida</h4>
              {generatedReceipt?.sandsMassWithHumidityCorrectionMultipliedByConcreteVolume?.map((sand) => {
                return (
                  <Row>
                    <Col xs={5}>{sand.formattedSandName}</Col>
                    <Col>{sand.formattedQuantity}</Col>
                  </Row>
                );
              })}

              <div className="d-flex justify-content-end">
                <Button type="submit">Gerar</Button>
              </div>
            </Form>
          </Modal.Body>
        </Modal>
      </Card.Body>
    </Card >
  );
}
